// frontend/src/components/layout/Sidebar.jsx
// Polar Night sidebar (DESIGN.md "Shell", IA §4): a slim glass column with
// role-filtered navigation. Reps see seven destinations; managers add Floor,
// Team, Campaigns and Reports; admins add Organization. 232 px with labels or
// a 60 px icon rail; a drawer under 768 px. The account block at the foot
// holds Workspaces (super admins), Settings, theme, "View as", Ask Nexus and
// sign out.

import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useUserRole } from "../../contexts/UserRoleContext";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { SKINS, getSkin, skinLabel } from "../../contexts/skins";
import SkinSwatch from "../ui/SkinSwatch";
import { isItemActive, navGroupsFor } from "../shell/navigation";
import { openAskNexus } from "../assistant/askNexusStore";
import { MS, usePrefersReducedMotion, usePresence } from "../ui/motion";
import { focusRowUnderPointer } from "../ui/menuFocus";
import WorkspaceMenuSection from "../shell/WorkspaceSwitcher";
import {
  BuildingIcon,
  CallsIcon,
  CampaignIcon,
  CheckIcon,
  CoachIcon,
  ContactsIcon,
  DealIcon,
  FloorIcon,
  HomeIcon,
  KeyboardIcon,
  LogoutIcon,
  MoonIcon,
  NexusGlyph,
  PhoneIcon,
  ReportsIcon,
  SettingsIcon,
  ShieldIcon,
  SidebarIcon,
  SparkIcon,
  SunIcon,
  TeamIcon,
  UnfoldIcon,
  UserIcon,
} from "../shell/icons";

const ICONS = {
  home: HomeIcon,
  phone: PhoneIcon,
  coach: CoachIcon,
  calls: CallsIcon,
  contacts: ContactsIcon,
  building: BuildingIcon,
  deal: DealIcon,
  floor: FloorIcon,
  team: TeamIcon,
  campaign: CampaignIcon,
  reports: ReportsIcon,
  shield: ShieldIcon,
};

const ROLE_LABELS = {
  agent: "Rep",
  fronter: "Fronter",
  closer: "Closer",
  manager: "Manager",
  admin: "Admin",
};

const fullName = (user) =>
  [user?.firstName ?? user?.first_name, user?.lastName ?? user?.last_name]
    .filter(Boolean)
    .join(" ");

const initialsOf = (user) => {
  const name = fullName(user);
  if (name) {
    return name
      .split(/\s+/)
      .map((part) => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  }
  return (user?.email || "?")[0].toUpperCase();
};

/**
 * "View as" choices. Admins preview the workspace as a rep, a manager or
 * themselves (the sidebar, Home and palette follow; the server still checks
 * their real role). Anyone else sees the roles their account holds.
 */
const ADMIN_VIEWS = [
  { id: "agent", name: "Rep" },
  { id: "manager", name: "Manager" },
  { id: "admin", name: "Admin" },
];

const viewAsOptions = (user, roleContext) => {
  if (["admin", "super_admin"].includes(String(user?.role || "").toLowerCase())) return ADMIN_VIEWS;
  return roleContext.getAvailableRoles?.() || [];
};

/** Arrow keys, Home and End move between a menu's rows. */
const moveMenuFocus = (event, root) => {
  if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key) || !root) return;
  event.preventDefault();
  const rows = [...root.querySelectorAll(".sg-menu-row:not(:disabled)")];
  if (!rows.length) return;
  const index = rows.indexOf(document.activeElement);
  let next = 0;
  if (event.key === "ArrowDown") next = index < 0 ? 0 : (index + 1) % rows.length;
  if (event.key === "ArrowUp") next = index < 0 ? rows.length - 1 : (index - 1 + rows.length) % rows.length;
  if (event.key === "End") next = rows.length - 1;
  rows[next]?.focus();
};

/** The account block: who is signed in, and what they can do as themselves. */
const AccountBlock = ({ rail, onNavigate }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth() || {};
  const theme = useTheme();
  const roleContext = useUserRole() || {};
  const [open, setOpen] = useState(false);
  const presence = usePresence(open, { exitMs: MS.exit });
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const openedByKey = useRef(false);
  const panelId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeEscape = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeEscape);
    // Opened from the keyboard, focus lands on the first row; a pointer
    // open leaves focus on the trigger so no ring flashes in the menu.
    if (openedByKey.current) panelRef.current?.querySelector(".sg-menu-row")?.focus();
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeEscape);
    };
  }, [open]);

  if (!user) return null;

  const name = fullName(user) || user.email || "Signed in";
  const role = roleContext.role;
  const roleLabel = `${ROLE_LABELS[role] || "Member"}${user.demo ? " · Demo" : ""}`;
  const viewRoles = viewAsOptions(user, roleContext);
  const canSwitchRole = viewRoles.length > 1 && roleContext.updateRole;
  const run = (action) => () => {
    setOpen(false);
    action();
  };
  const avatar = (className) =>
    user.avatarUrl ? (
      <img src={user.avatarUrl} alt="" className={className} />
    ) : (
      <span className={className} aria-hidden="true">
        {initialsOf(user)}
      </span>
    );

  return (
    <div className="sg-account" ref={rootRef} data-open={open || undefined}>
      <button
        ref={triggerRef}
        type="button"
        className="sg-account-trigger"
        aria-label={rail ? `Account: ${name}` : "Account menu"}
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="menu"
        data-tip={rail ? name : undefined}
        onKeyDown={(event) => {
          if (["Enter", " ", "ArrowUp", "ArrowDown"].includes(event.key)) openedByKey.current = true;
          if ((event.key === "ArrowUp" || event.key === "ArrowDown") && !open) {
            event.preventDefault();
            setOpen(true);
            return;
          }
          // However the menu was opened (a click leaves focus here), the
          // arrows, Home and End move into its rows, as in RowMenu.
          if (open) moveMenuFocus(event, panelRef.current);
        }}
        onPointerDown={() => {
          openedByKey.current = false;
        }}
        onClick={() => setOpen((value) => !value)}
      >
        {avatar("sg-account-avatar")}
        <span className="sg-account-text sg-side-label">
          <span className="sg-account-name">{name}</span>
          <span className="sg-account-role">{roleLabel}</span>
        </span>
        <UnfoldIcon size={14} className="sg-account-unfold sg-side-label" />
      </button>

      {presence.mounted && (
        <div
          ref={panelRef}
          id={panelId}
          className="sg-account-menu sg-menu-panel"
          role="menu"
          aria-label="Account"
          data-state={presence.state}
          onAnimationEnd={presence.onAnimationEnd}
          onKeyDown={(event) => moveMenuFocus(event, panelRef.current)}
          onMouseMove={focusRowUnderPointer(".sg-menu-row")}
        >
          <div className="sg-menu-identity" role="presentation">
            {avatar("sg-account-avatar sg-account-avatar-lg")}
            <div className="min-w-0 flex-1">
              <p className="truncate text-[13px] font-medium text-sg-ink">{name}</p>
              {user.email && (
                <p className="truncate font-mono text-[11px] text-sg-ink-3">{user.email}</p>
              )}
            </div>
          </div>
          <button
            type="button"
            role="menuitem"
            className="sg-menu-row"
            onClick={run(() => {
              onNavigate?.();
              navigate("/settings");
            })}
          >
            <UserIcon size={15} className="sg-menu-icon" />
            <span className="flex-1">Profile and settings</span>
          </button>
          {theme.skinForcesDark ? (
            <button type="button" role="menuitem" className="sg-menu-row" disabled aria-disabled="true">
              <MoonIcon size={15} className="sg-menu-icon" />
              <span className="flex-1">{getSkin(theme.skin).name} is dark only</span>
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              className="sg-menu-row"
              onClick={run(() => {
                theme.toggleDarkMode();
              })}
            >
              {theme.darkMode ? (
                <SunIcon size={15} className="sg-menu-icon" />
              ) : (
                <MoonIcon size={15} className="sg-menu-icon" />
              )}
              <span className="flex-1">Use {theme.darkMode ? "light" : "dark"} theme</span>
            </button>
          )}
          <WorkspaceMenuSection user={user} onClose={() => setOpen(false)} />
          <div role="group" aria-label="Interface" className="sg-menu-section">
            <div className="sg-menu-head sg-micro">Interface</div>
            {SKINS.map((option) => (
              <button
                key={option.id}
                type="button"
                role="menuitemradio"
                aria-checked={theme.skin === option.id}
                className="sg-menu-row"
                onClick={run(() => theme.setSkin(option.id))}
              >
                <SkinSwatch skin={option} />
                <span className="flex-1">{skinLabel(option.id)}</span>
                {theme.skin === option.id && <CheckIcon size={14} className="sg-menu-check" />}
              </button>
            ))}
          </div>
          <button
            type="button"
            role="menuitem"
            className="sg-menu-row"
            onClick={run(() => {
              onNavigate?.();
              openAskNexus("ask");
            })}
          >
            <SparkIcon size={15} className="sg-menu-icon" />
            <span className="flex-1">Ask your coach</span>
            <span className="sg-menu-keys" aria-hidden="true">
              <kbd className="sg-kbd">{modKey()}</kbd>
              <kbd className="sg-kbd">J</kbd>
            </span>
          </button>
          <button
            type="button"
            role="menuitem"
            className="sg-menu-row"
            onClick={run(() => {
              // KeyboardShortcuts listens for "?" on window.
              window.dispatchEvent(new KeyboardEvent("keydown", { key: "?" }));
            })}
          >
            <KeyboardIcon size={15} className="sg-menu-icon" />
            <span className="flex-1">Keyboard shortcuts</span>
            <kbd className="sg-kbd" aria-hidden="true">
              ?
            </kbd>
          </button>
          {canSwitchRole && (
            <div role="group" aria-label="View workspace as" className="sg-menu-section">
              <div className="sg-menu-head sg-micro">View as</div>
              {viewRoles.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={role === option.id}
                  className="sg-menu-row"
                  onClick={run(() => roleContext.updateRole(option.id))}
                >
                  <span className="flex-1">{option.name}</span>
                  {role === option.id && <CheckIcon size={14} className="sg-menu-check" />}
                </button>
              ))}
            </div>
          )}
          <div className="sg-menu-section" role="group">
            <button
              type="button"
              role="menuitem"
              className="sg-menu-row"
              onClick={run(() => {
                onNavigate?.();
                logout?.();
                navigate("/login");
              })}
            >
              <LogoutIcon size={15} className="sg-menu-icon" />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const modKey = () =>
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || "")
    ? "⌘"
    : "Ctrl";

/**
 * The active-item indicator: one element that glides between nav items on
 * transform (DESIGN.md "Motion": indicators 200 ms). Every route mounts its
 * own Layout, so the last position is kept at module scope and a remounted
 * sidebar starts where the old one stopped, then glides to the new item.
 */
let lastIndicator = null;

const useNavIndicator = (navRef, activePath, layoutKey) => {
  const [box, setBox] = useState(null);
  const [animate, setAnimate] = useState(false);
  const pathRef = useRef(null);
  const pendingGlideRef = useRef(false);

  const measure = useCallback(
    (glide) => {
      const nav = navRef.current;
      const item = nav?.querySelector('.sg-side-item[data-active]');
      if (!item) {
        setBox(null);
        return;
      }
      const next = {
        y: item.offsetTop,
        x: item.offsetLeft,
        width: item.offsetWidth,
        height: item.offsetHeight,
      };
      setAnimate(glide);
      setBox(next);
      lastIndicator = { ...next, path: activePath };
    },
    [navRef, activePath],
  );

  useLayoutEffect(() => {
    const routeChanged = pathRef.current !== null && pathRef.current !== activePath;
    const first = pathRef.current === null;
    pathRef.current = activePath;
    // A remounted sidebar starts from the previous item, then glides. The
    // pending flag survives an effect re-run before the glide frame (React
    // StrictMode runs mount effects twice in development).
    if (
      pendingGlideRef.current ||
      (first && lastIndicator && lastIndicator.path !== activePath)
    ) {
      pendingGlideRef.current = true;
      setAnimate(false);
      setBox(lastIndicator);
      const frame = requestAnimationFrame(() => {
        pendingGlideRef.current = false;
        measure(true);
      });
      return () => cancelAnimationFrame(frame);
    }
    measure(routeChanged);
    return undefined;
  }, [activePath, layoutKey, measure]);

  // Rail toggles, fonts and viewport changes re-measure without gliding.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav || typeof ResizeObserver === "undefined") return undefined;
    // The observer also reports once on observe; only a real size change
    // re-measures, so a remount's glide is not cut short.
    let size = `${nav.clientWidth}x${nav.scrollHeight}`;
    const observer = new ResizeObserver(() => {
      const next = `${nav.clientWidth}x${nav.scrollHeight}`;
      if (next === size) return;
      size = next;
      measure(false);
    });
    observer.observe(nav);
    return () => observer.disconnect();
  }, [navRef, measure]);

  return { box, animate };
};

/**
 * Rail tooltips: one glass flyout to the right of the rail, shown on hover
 * (after 80 ms) and on keyboard focus, with the label and its "G then" key.
 * Portaled to <body>: the sidebar's backdrop-filter would otherwise become
 * the containing block of a fixed element.
 */
const useRailTip = (enabled) => {
  const [tip, setTip] = useState(null);
  const timer = useRef(null);
  const presence = usePresence(Boolean(tip?.visible), { exitMs: MS.exit });

  const hide = useCallback(() => {
    window.clearTimeout(timer.current);
    setTip((current) => (current ? { ...current, visible: false } : current));
  }, []);

  const show = useCallback(
    (element, delay) => {
      if (!enabled || !element) return;
      const label = element.getAttribute("data-tip");
      if (!label) return;
      window.clearTimeout(timer.current);
      const place = () => {
        const rect = element.getBoundingClientRect();
        setTip({
          label,
          hint: element.getAttribute("data-key"),
          top: rect.top + rect.height / 2,
          left: rect.right + 10,
          visible: true,
        });
      };
      // Moving between items while one tip is up swaps it at once.
      if (delay && !tip?.visible) timer.current = window.setTimeout(place, delay);
      else place();
    },
    [enabled, tip?.visible],
  );

  useEffect(() => {
    if (!enabled) hide();
  }, [enabled, hide]);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const node =
    presence.mounted && tip
      ? createPortal(
          // The outer layer travels between items on transform; the inner
          // tip plays the enter and exit keyframes.
          <div
            className="sg-rail-tip-layer"
            aria-hidden="true"
            style={{ transform: `translate3d(${tip.left}px, ${tip.top}px, 0)` }}
          >
            <div
              className="sg-rail-tip"
              data-state={presence.state}
              onAnimationEnd={presence.onAnimationEnd}
            >
              <span>{tip.label}</span>
              {tip.hint && (
                <span className="sg-rail-tip-keys">
                  <kbd className="sg-kbd">G</kbd>
                  <kbd className="sg-kbd">{tip.hint.toUpperCase()}</kbd>
                </span>
              )}
            </div>
          </div>,
          document.body,
        )
      : null;

  const handlers = enabled
    ? {
        onPointerOver: (event) => {
          const target = event.target.closest?.("[data-tip]");
          if (target) show(target, 80);
          else hide();
        },
        onPointerLeave: hide,
        onFocus: (event) => {
          if (event.target.matches?.(":focus-visible")) show(event.target.closest?.("[data-tip]"), 0);
        },
        onBlur: hide,
        onClick: hide,
      }
    : {};

  return { node, handlers };
};

const Sidebar = ({
  open,
  isMobile = false,
  toggleSidebar,
  collapsed = false,
  onToggleCollapsed,
}) => {
  const location = useLocation();
  const sidebarRef = useRef(null);
  const navRef = useRef(null);
  const { role = "fronter" } = useUserRole() || {};
  const { user } = useAuth() || {};
  const reduced = usePrefersReducedMotion();
  const rail = collapsed && !isMobile;

  useEffect(() => {
    if (!isMobile || !open) return undefined;
    const previousFocus = document.activeElement;
    sidebarRef.current?.querySelector("button, a")?.focus();
    return () => previousFocus?.focus();
  }, [isMobile, open]);

  // Labels cross-fade only when the rail is toggled, never on a remount.
  const [railChanged, setRailChanged] = useState(false);
  const railRef = useRef(rail);
  useEffect(() => {
    if (railRef.current === rail) return undefined;
    railRef.current = rail;
    setRailChanged(true);
    const timer = window.setTimeout(() => setRailChanged(false), 400);
    return () => window.clearTimeout(timer);
  }, [rail]);

  const closeOnMobile = () => {
    if (isMobile) toggleSidebar?.();
  };

  const groups = navGroupsFor(role, user);
  // A rep's seven entries read best as one quiet list; group names help once
  // the manager and admin destinations join.
  const showGroupLabels = groups.length > 2;
  const orgName =
    user?.organization?.name || user?.organizationName || user?.orgName || "";

  const activeItem = groups
    .flatMap((group) => group.items)
    .find((item) => isItemActive(item, location.pathname));
  const { box, animate } = useNavIndicator(
    navRef,
    activeItem?.path ?? null,
    `${rail}-${isMobile}-${groups.length}`,
  );
  const tip = useRailTip(rail);

  const renderLink = (item) => {
    const Icon = ICONS[item.icon] || HomeIcon;
    const active = isItemActive(item, location.pathname);
    return (
      // A plain Link: NavLink only sets aria-current on its own path match,
      // and items stay current across the old routes they absorb (`match`).
      <Link
        key={item.label}
        to={item.path}
        onClick={closeOnMobile}
        aria-label={rail ? item.label : undefined}
        aria-keyshortcuts={item.key ? `G ${item.key.toUpperCase()}` : undefined}
        aria-current={active ? "page" : undefined}
        data-active={active || undefined}
        data-tip={rail ? item.label : undefined}
        data-key={item.key}
        className="sg-side-item"
      >
        <span className="sg-side-icon">
          <Icon />
        </span>
        <span className="sg-side-label">{item.label}</span>
        {/* "G then N", drawn by CSS from data-key so the link's text stays
            its label. */}
        {item.key && !isMobile && (
          <span className="sg-side-hint" data-key={item.key.toUpperCase()} aria-hidden="true" />
        )}
      </Link>
    );
  };

  const collapseButton = !isMobile && onToggleCollapsed && (
    <button
      type="button"
      className="sg-side-collapse"
      onClick={onToggleCollapsed}
      aria-label={rail ? "Expand sidebar" : "Collapse sidebar"}
      aria-pressed={rail}
      aria-keyshortcuts="["
      data-tip={rail ? "Expand sidebar" : undefined}
      data-hint={rail ? undefined : "Collapse sidebar"}
      data-hint-keys={rail ? undefined : "["}
    >
      <SidebarIcon />
    </button>
  );

  return (
    <>
      {isMobile && (
        <button
          type="button"
          className="nx-sidebar-backdrop"
          aria-label="Close navigation"
          tabIndex={open ? 0 : -1}
          aria-hidden={open ? undefined : true}
          data-open={open || undefined}
          onClick={toggleSidebar}
        />
      )}
      <aside
        id="workspace-navigation"
        ref={sidebarRef}
        // Open on a phone it is a modal drawer (focus is held inside it).
        role={isMobile && open ? "dialog" : undefined}
        aria-modal={isMobile && open ? true : undefined}
        aria-label={isMobile && open ? "Navigation" : undefined}
        inert={isMobile && !open ? "" : undefined}
        aria-hidden={isMobile && !open ? true : undefined}
        data-rail={rail || undefined}
        data-rail-changed={railChanged || undefined}
        data-mobile={isMobile || undefined}
        data-open={open || undefined}
        onKeyDown={(event) => {
          if (!isMobile || !open || event.key !== "Tab") return;
          const controls = [...sidebarRef.current.querySelectorAll("a, button")];
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last?.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first?.focus();
          }
        }}
        className="nx-workspace-sidebar sg-sidebar"
        {...tip.handlers}
      >
        <div className="sg-side-brand">
          <NavLink
            to="/dashboard"
            onClick={closeOnMobile}
            className="sg-side-brandlink"
            aria-label="NexusDialer home"
            data-tip={rail ? "Home" : undefined}
          >
            <span className="sg-side-mark">
              <NexusGlyph size={14} />
            </span>
            <span className="sg-side-label sg-side-brandtext">
              <span className="sg-side-wordmark">NexusDialer</span>
              {orgName && <span className="sg-side-org">{orgName}</span>}
            </span>
          </NavLink>
          {!rail && collapseButton}
        </div>

        <nav ref={navRef} className="sg-side-nav" role="navigation" aria-label="Main navigation">
          {box && (
            <span
              className="sg-side-indicator"
              aria-hidden="true"
              data-glide={(animate && !reduced) || undefined}
              style={{
                width: box.width,
                height: box.height,
                transform: `translate3d(${box.x}px, ${box.y}px, 0)`,
              }}
            />
          )}
          {groups.map((group) => (
            <div key={group.label} className="sg-side-group">
              {showGroupLabels && (
                <div className="sg-side-grouplabel" aria-hidden={rail || undefined}>
                  {group.label}
                </div>
              )}
              {group.items.map(renderLink)}
            </div>
          ))}
        </nav>

        {/* The line state lives once, in the call dock. */}
        <div className="sg-side-foot">
          <NavLink
            to="/settings"
            onClick={closeOnMobile}
            aria-label={rail ? "Settings" : undefined}
            data-tip={rail ? "Settings" : undefined}
            data-active={location.pathname.startsWith("/settings") || undefined}
            className="sg-side-item sg-side-item-solo"
          >
            <span className="sg-side-icon">
              <SettingsIcon />
            </span>
            <span className="sg-side-label">Settings</span>
          </NavLink>
          <AccountBlock rail={rail} onNavigate={closeOnMobile} />
          {rail && collapseButton}
        </div>
      </aside>
      {tip.node}
    </>
  );
};

export default Sidebar;
